import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';

import { Stack } from '_lib/components/Stack/Stack';

import { Typography, TypographyProps } from '_lib/components/Typography/Typography';

import { API_URL } from '_lib/global/config';

import { HeaderBox, HeaderBoxProps } from '../HeaderBox/HeaderBox';

const InfoItemDisplay = (props: {
  headerBoxProps: HeaderBoxProps;
  typographyProps: TypographyProps;
  hideDivider?: boolean;
}) => {
  return (
    <Stack useFlexGap direction='column' spacing={1} sx={{ height: '100%', width: '100%' }}>
      <HeaderBox {...props.headerBoxProps}>
        <Typography {...props.typographyProps} />
      </HeaderBox>
      {!props.hideDivider && <Divider />}
    </Stack>
  );
};

export const ApiInfo = () => {
  const api = useContext(ApiContext);

  const healthCheckQuery = api.appConfig.useHealthCheckQuery();
  const versionQuery = api.appConfig.useVersionQuery();

  const [displayState, setDisplayState] = useState({
    showClientIP: false,
    showRequestIP: false,
    showRequestIPs: false
  });

  if (healthCheckQuery.error || versionQuery.error) {
    return (
      <ManualErrorBoundary
        errorState={{ error: healthCheckQuery.error || versionQuery.error, message: 'Error fetching API Info' }}
        onRefresh={async () => {
          healthCheckQuery.refetch();
          versionQuery.refetch();
        }}
        sx={{ padding: 2 }}
      />
    );
  }

  if (healthCheckQuery.isFetching || versionQuery.isFetching)
    return (
      <FlexBox sx={{ position: 'relative', minHeight: '650px' }}>
        <BackdropSpinner open />
      </FlexBox>
    );

  const healthCheckData = healthCheckQuery.data;

  const getServerStatusText = () => {
    if (healthCheckData?.isFailover) return 'failover';
    else if (healthCheckData?.isFailover === false) return 'primary';
    else return 'Unknown';
  };

  return (
    <FlexBox sx={{ flexDirection: 'column', width: '100%', height: '100%' }}>
      <Stack useFlexGap direction='column' spacing={2} padding={2} sx={{ height: '100%', width: '100%' }}>
        <InfoItemDisplay headerBoxProps={{ headerText: 'UI Client Base URL' }} typographyProps={{ text: API_URL }} />
        <InfoItemDisplay
          headerBoxProps={{ headerText: 'Hostname' }}
          typographyProps={{ text: healthCheckData?.hostname || 'Unknown' }}
        />
        <InfoItemDisplay
          headerBoxProps={{ headerText: 'Subdomain' }}
          typographyProps={{ text: healthCheckData?.subdomains?.join('.') || 'None' }}
        />
        <InfoItemDisplay
          headerBoxProps={{ headerText: 'API Server Status' }}
          typographyProps={{ text: getServerStatusText(), color: healthCheckData?.isFailover ? 'warning' : 'success' }}
        />
        <InfoItemDisplay
          headerBoxProps={{
            headerText: 'Request Client IP',
            visibilityIcon: {
              isVisible: displayState.showClientIP,
              onToggleVisibility: () => setDisplayState((prev) => ({ ...prev, showClientIP: !prev.showClientIP }))
            }
          }}
          typographyProps={{
            text: displayState.showClientIP ? healthCheckData?.clientIP || 'Unknown' : '************'
          }}
        />
        <InfoItemDisplay
          headerBoxProps={{
            headerText: 'Request IP',
            visibilityIcon: {
              isVisible: displayState.showRequestIP,
              onToggleVisibility: () => setDisplayState((prev) => ({ ...prev, showRequestIP: !prev.showRequestIP }))
            }
          }}
          typographyProps={{
            text: displayState.showRequestIP ? healthCheckData?.requestIp || 'Unknown' : '************'
          }}
        />
        <InfoItemDisplay
          headerBoxProps={{
            headerText: 'Request IP Address(es)',
            visibilityIcon: {
              isVisible: displayState.showRequestIPs,
              onToggleVisibility: () => setDisplayState((prev) => ({ ...prev, showRequestIPs: !prev.showRequestIPs }))
            }
          }}
          typographyProps={{
            text: displayState.showRequestIPs ? healthCheckData?.requestIps?.join(', ') || 'None' : '************'
          }}
        />
        <InfoItemDisplay
          headerBoxProps={{ headerText: 'Uptime' }}
          typographyProps={{ text: healthCheckData?.uptime ? `${healthCheckData.uptime} seconds` : 'Unknown' }}
        />
        <InfoItemDisplay
          headerBoxProps={{ headerText: 'Version' }}
          typographyProps={{ text: versionQuery.data?.version || 'Unknown' }}
          hideDivider
        />
      </Stack>
    </FlexBox>
  );
};
